import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { TurnsCapacity } from '../../../types';
import { Delta } from '../../shared/Delta';
import { DrillToolbarStat } from '../../shared/DrillToolbar';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-capacity.css';

function fmtTurns(v: number): string {
  return `${v.toLocaleString(i18n.language?.startsWith('de') ? 'de-DE' : 'en-GB', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  })}×`;
}

/**
 * Turns hero companions: the calendar-day figure next to the per-operating-day
 * hero, and seat-hour use (the Occupancy hero's number) with the sentence
 * that squares the two.
 */
export function DrillTurnsHeroStats({
  capacity,
  compareLabel,
}: {
  capacity?: TurnsCapacity;
  /** The compare period, when one is on: seat-hour use gets its change. */
  compareLabel?: string;
}) {
  if (!capacity) return null;
  const comparePct = compareLabel ? capacity.compareSeatHourPct : undefined;
  const pctChange =
    comparePct != null ? Math.round((capacity.seatHourPct - comparePct) * 10) / 10 : null;
  return (
    <>
      <DrillToolbarStat
        label={i18n.t('performance:drillUi.perCalendarDay')}
        value={fmtTurns(capacity.perCalendarDay)}
        secondary
      />
      <span
        className="dcap-hero-stat"
        title={[
          i18n.t('performance:drillUi.seatHourUseTip', {
            used: fmt.num(capacity.occupiedSeatHours),
            offered: fmt.num(capacity.availableSeatHours),
          }),
          capacity.reconciliation ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <DrillToolbarStat
          label={i18n.t('performance:drillUi.seatHourUse')}
          value={`${capacity.seatHourPct}%`}
          secondary
          trend={
            pctChange != null && comparePct != null ? (
              <span
                className="drill-toolbar-sub-delta"
                title={`${compareLabel}: ${comparePct}%`}
              >
                <Delta value={pctChange} suffix="pt" size={12} />
              </span>
            ) : null
          }
        />
      </span>
    </>
  );
}

function ShiftList({ rows, detail }: { rows: TurnsCapacity['byShift']; detail: boolean }) {
  const max = Math.max(0.0001, ...rows.map(r => r.turns));
  return (
    <ul className="dcap-turn-shifts">
      {rows.map(r => (
        <li key={r.label} className={r.other ? 'is-other' : undefined}>
          <span className="dcap-shift-name">{r.label}</span>
          <span className="dcap-turn-track" aria-hidden>
            <span
              className="dcap-turn-fill"
              style={{ width: `${Math.max(2, (r.turns / max) * 100)}%` }}
            />
          </span>
          <span className="dcap-shift-val">{fmtTurns(r.turns)}</span>
          <span className="dcap-n">
            {detail
              ? i18n.t('performance:drillUi.turnsShiftSeatDays', {
                  guests: fmt.num(r.guests),
                  seatDays: fmt.num(r.seatDays),
                })
              : i18n.t('performance:drillUi.turnsShiftN', {
                  n: fmt.num(r.n),
                  guests: fmt.num(r.guests),
                })}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Turns per shift on each shift's own seats and the days it served guests,
 * with the reconciliation sentence on top. Every row prints its n.
 */
export function DrillTurnsShiftSection({
  capacity,
  outletCount,
}: {
  metricKey: string;
  capacity?: TurnsCapacity;
  /** Venues in scope: one venue counts days, several count venue-days. */
  outletCount: number;
}) {
  const rows = capacity?.byShift ?? [];

  const inspector = useMemo(
    () =>
      capacity ? (
        <div className="dcap-turns-inspector">
          {capacity.reconciliation ? (
            <p className="dcap-reconcile">{capacity.reconciliation}</p>
          ) : null}
          <ShiftList rows={capacity.byShift} detail />
        </div>
      ) : null,
    [capacity],
  );

  if (!capacity || !rows.length) return null;

  const meta =
    outletCount > 1
      ? i18n.t('performance:drillUi.turnsByShiftMetaMany', {
          n: fmt.num(capacity.operatingDays),
        })
      : i18n.t('performance:drillUi.turnsByShiftMetaOne', {
          n: fmt.num(capacity.operatingDays),
          calendar: fmt.num(capacity.calendarDays),
        });

  return (
    <DrillTile
      sectionId="turnsByShift"
      span={6}
      title={i18n.t('performance:drillUi.turnsByShift')}
      meta={meta}
      inspector={inspector}
    >
      <div className="dcap-turns-tile">
        {capacity.reconciliation ? (
          <p className="dcap-reconcile">{capacity.reconciliation}</p>
        ) : null}
        <ShiftList rows={rows} detail={false} />
      </div>
    </DrillTile>
  );
}
